import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BalansEnResultatenrekening,
  BokkzAlgDataService,
  BokkzVpbDataService,
  BokkzVpbLogic,
  CorporateTaxEntity,
  KolommenbalansService,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, Observable, shareReplay, tap} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-fiscale-eenheid-samenstelling',
  templateUrl: './fiscale-eenheid-samenstelling.component.html',
  styleUrls: ['./fiscale-eenheid-samenstelling.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    CommonModule
  ]
})
export class FiscaleEenheidSamenstellingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  private bokkzDataService = inject(BokkzAlgDataService);
  private kolommenbalansService = inject(KolommenbalansService);
  public vpbService = inject(BokkzVpbLogic);

  public dialogVisible = false;
  public options: SelectFormControlItem<string>[] = [];
  private clientId = '';
  private boekjaarBegindatum: Date | undefined;
  private boekjaarEinddatum: Date | undefined;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: false, label: 'Moeder'},
    {value: true, label: 'Dochter'},
  ];

  public enkelvoudigFg = new FormGroup({
    enkelvoudigecijfers: new FormArray<FormGroup>([])
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (!data.aangifte) return;
        this.boekjaarBegindatum = data.aangifte.boekjaar?.begindatum;
        this.boekjaarEinddatum = data.aangifte.boekjaar?.einddatum;
        this.syncFormArray(data.aangifte);
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  get enkelvoudigecijfers(): FormArray {
    return this.enkelvoudigFg.get('enkelvoudigecijfers') as FormArray;
  }

  addRij(): void {
    this.enkelvoudigecijfers.push(this.createFormGroup());
  }

  verwijderRij(index: number): void {
    this.enkelvoudigecijfers.removeAt(index);
    this.saveForm();
  }

  createFormGroup(item?: BalansEnResultatenrekening, name?: string): FormGroup {
    return new FormGroup({
      naamVennootschap: new FormControl<string | null>(item ? item.naamVennootschap : name ?? null),
      boekjaarVennootschap: new FormGroup({
      begindatum: new FormControl<Date | null>(item?.boekjaarVennootschap?.begindatum ?? this.boekjaarBegindatum ?? null),
      einddatum: new FormControl<Date | null>(item?.boekjaarVennootschap?.einddatum ?? this.boekjaarEinddatum ?? null),
      }),
      fiscaalNummer: new FormControl<string | null>(item?.fiscaalNummer ?? null),
      omschrijvingActiviteiten: new FormControl<string | null>(item?.omschrijvingActiviteiten ?? 'Activiteiten'),
      agrarischeActiviteiten: new FormControl<boolean | null>(item?.agrarischeActiviteiten ?? false),
      dochtermaatschappijFiscaleEenheid: new FormControl<boolean | null>(item?.dochtermaatschappijFiscaleEenheid ?? true),
      administratieId: new FormControl<string | null>(item?.administratieId ?? null),
      goodwill: new FormControl(item?.goodwill ?? null),
      overigeImmaterieleActiva: new FormControl(item?.overigeImmaterieleActiva ?? null),
      gebouwenEnTerreinen: new FormControl(item?.gebouwenEnTerreinen ?? null),
      machinesEnInstallaties: new FormControl(item?.machinesEnInstallaties ?? null),
      andereVasteActiva: new FormControl(item?.andereVasteActiva ?? null),
      deelnemingen: new FormControl(item?.deelnemingen ?? null),
      vorderingenLangGroepsmaatschappijen: new FormControl(item?.vorderingenLangGroepsmaatschappijen ?? null),
      vorderingenLangParticipanten: new FormControl(item?.vorderingenLangParticipanten ?? null),
      overigeFinancieelVasteActiva: new FormControl(item?.overigeFinancieelVasteActiva ?? null),
      voorraden: new FormControl(item?.voorraden ?? null),
      onderhandenWerk: new FormControl(item?.onderhandenWerk ?? null),
      vorderingDebiteuren: new FormControl(item?.vorderingDebiteuren ?? null),
      vorderingenOmzetbelasting: new FormControl(item?.vorderingenOmzetbelasting ?? null),
      vorderingenKortGroepsmaatschappijen: new FormControl(item?.vorderingenKortGroepsmaatschappijen ?? null),
      vorderingenKortParticipanten: new FormControl(item?.vorderingenKortParticipanten ?? null),
      vorderingenOverig: new FormControl(item?.vorderingenOverig ?? null),
      effecten: new FormControl(item?.effecten ?? null),
    });
  }

  getAdministration(i: number): void {
    const vennootschap = this.enkelvoudigecijfers.at(i);
    this.kolommenbalansService.getUpdatedAangifteMetAdministratie(
      vennootschap.value.administratieId,
      this.clientId,
      {} as CorporateTaxEntity,
      false,
      this.clientId
    ).then((entity) => {
      if (entity.aangifte?.identificatie) {
      }
    });
  }

  private syncFormArray(aangifte: VpbAangifte): void {
    const raw = [...(aangifte.enkelvoudigeResultatenFiscaleEenheid ?? [])];
    const nonEliminatieRows = raw.filter(row => row.naamVennootschap !== 'Eliminatie');
    const eliminatieRow = raw.find(row => row.naamVennootschap === 'Eliminatie');

    const parentIndex = nonEliminatieRows.findIndex(row => row.dochtermaatschappijFiscaleEenheid === false);
    const parentRow = parentIndex >= 0
      ? nonEliminatieRows.splice(parentIndex, 1)[0]
      : ({
        ...aangifte.balansEnResultatenrekening,
        naamVennootschap:
          aangifte.balansEnResultatenrekening?.naamVennootschap ??
          aangifte.identificatie?.naamLichaam,
        dochtermaatschappijFiscaleEenheid: false,
      } as BalansEnResultatenrekening);

    const lijst: BalansEnResultatenrekening[] = [
      parentRow,
      ...nonEliminatieRows,
      eliminatieRow ?? ({naamVennootschap: 'Eliminatie'} as BalansEnResultatenrekening),
    ];

    const formArray = this.enkelvoudigecijfers;

    while (formArray.length > lijst.length) formArray.removeAt(formArray.length - 1);
    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue(lijst[i], {emitEvent: false});
      } else {
        formArray.push(this.createFormGroup(lijst[i]), {emitEvent: false});
      }
    }
  }

  ngOnInit(): void {
    this.bokkzDataService.options.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.options = value;
    });
    this.enkelvoudigFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.enkelvoudigFg.updateValueAndValidity({emitEvent: false});
    const enkelvoudigeResultatenFiscaleEenheid = this.enkelvoudigecijfers.getRawValue() as BalansEnResultatenrekening[];

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) return currentEntity;
      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          enkelvoudigeResultatenFiscaleEenheid,
        } as VpbAangifte,
      };
    });
  }
}
